import { add, div, int, isInteger, mul, neg, rat, sub, type Rational } from '../../math/rational.ts';
import type { Rng } from '../../math/rng.ts';
import type { GeneratedProblemCore, ProblemTemplate } from '../../types.ts';
import {
  cappedPow,
  fitsPhone,
  ipow,
  logOf,
  poly,
  problem,
  ratMarkup,
  retry,
  rpow,
  safeRat,
  scaled,
  seriesTerm,
  SHOWABLE,
  type Candidate,
  type Instruction,
  type Term,
} from './helpers-g11-12.ts';

/**
 * Grade 12 templates (CAPS FET, final year): logarithms (definition, simple
 * equations, laws), geometric sequences, arithmetic and geometric series,
 * sigma notation, function values and inverses of linear functions.
 *
 * Ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 * Base-10 logs are written `log` (the CAPS convention); other bases explicitly.
 */

const SOLVE_X: Instruction = { key: 'solve', params: { v: 'x' } };

// ───────────────────────────── g12.log-eval ─────────────────────────────

/** log_(p^u) (p^v) = v/u with u ∤ v, e.g. log₄ 8 = 3/2 — the fractional top of g12.log-eval. */
function sharedPrimeLog(rng: Rng): GeneratedProblemCore {
  return retry(
    60,
    () => {
      const family = rng.pick([
        { prime: 2, maxPow: 5 },
        { prime: 3, maxPow: 4 },
      ]);
      const u = rng.int(2, 3); // base 4, 8, 9 or 27
      const v = rng.intExcept(-2, family.maxPow, [0]);
      if (v % u === 0) return null; // whole-number answers are the main branch's job
      const arg = v > 0 ? `${ipow(family.prime, v)}` : `\\frac{1}{${ipow(family.prime, -v)}}`;
      const answer = rat(v, u);
      return problem(`${logOf(ipow(family.prime, u))} ${arg} = \\box`, answer, [
        int(v), // rewrote in the prime base but forgot to divide by the base's own power
        rat(u, v), // flipped the fraction
        neg(answer), // sign slip
      ]);
    },
    () => problem('\\log_{4} 8 = \\box', rat(3, 2), [int(3), rat(2, 3), rat(-3, 2)]),
  );
}

/**
 * log_b(b^k) = k, the argument written as a number (or 1/b^|k|). The gentle band
 * starts at k = 2 (log 10 = 1 is too bare for Grade 12); k = 0, negative k and
 * shared-prime bases (log₄ 8 = 3/2) come in above it.
 */
const logEval: ProblemTemplate = {
  id: 'g12.log-eval',
  grade: 12,
  title: 'Evaluate log_b(b^k)',
  parTimeMs: 12000,
  generate(rng, d) {
    if (d >= 0.7 && rng.chance(0.35)) return sharedPrimeLog(rng);
    const argMax = scaled(d, 100, 1024);
    return retry(
      100,
      () => {
        const base = rng.pick(d < 0.35 ? [2, 3, 5, 10] : [2, 3, 4, 5, 10]);
        let k: number;
        if (d < 0.35) k = rng.int(2, 6);
        else if (rng.chance(0.08)) k = 0;
        else k = rng.chance(0.2 + 0.4 * d) ? -rng.int(1, 3) : rng.int(2, 8);
        const power = ipow(base, Math.abs(k));
        if (power > argMax) return null;
        const arg = k < 0 ? `\\frac{1}{${power}}` : `${power}`;
        let candidates: Candidate[];
        if (k > 0) {
          candidates = [
            int(k - 1), // counted the multiplication signs, not the factors: 2 × 2 × 2 → 2
            int(k + 1), // off by one the other way (base 10: counted the digits, not the zeros)
            int(power / base), // divided the number by the base instead of counting factors
          ];
        } else if (k < 0) {
          candidates = [
            int(-k), // ignored the reciprocal (sign slip)
            rat(1, -k), // took log(1/n) as 1/log n
            int(k + 1), // off by one
            power <= 100 ? int(-power) : null, // gave minus the denominator instead of its power
          ];
        } else {
          candidates = [
            int(1), // mixed up log_b 1 with log_b b = 1
            int(base), // thought log_b 1 = b
          ];
        }
        return problem(`${logOf(base)} ${arg} = \\box`, int(k), candidates);
      },
      () => problem('\\log_{3} 81 = \\box', int(4), [int(3), int(5), int(27)]),
    );
  },
};

// ───────────────────────────── g12.log-eq ─────────────────────────────

/** log_b(ax + k) = n ⇒ ax + k = bⁿ. `a` > 1 and negative n (fraction answers) only near the top. */
const logEq: ProblemTemplate = {
  id: 'g12.log-eq',
  grade: 12,
  title: 'Solve log_b(ax ± k) = n',
  parTimeMs: 14000,
  generate(rng, d) {
    const powerMax = scaled(d, 100, 625);
    const kMax = scaled(d, 6, 12);
    return retry(
      100,
      () => {
        const fractional = d >= 0.7 && rng.chance(0.25);
        const base = rng.pick(d < 0.35 ? [2, 3, 10] : fractional ? [2, 3, 4, 5] : [2, 3, 4, 5, 10]);
        const n = fractional ? -rng.int(1, 2) : d >= 0.35 && rng.chance(0.08) ? 0 : rng.int(1, 4);
        const power = rpow(int(base), n);
        if (power.n > powerMax) return null;
        const a = !fractional && d >= 0.6 && rng.chance(0.4) ? rng.int(2, 5) : 1;
        const k = rng.intExcept(-kMax, kMax, [0]);
        const x = div(sub(power, int(k)), int(a));
        if (!fractional && !isInteger(x)) return null;
        if (x.n === 0 || (d < 0.35 && x.n < 0)) return null;
        const whole = isInteger(power) ? power.n : null;
        return problem(
          `${logOf(base)}(${poly([[a, 1], [k, 0]])}) = ${n}`,
          x,
          [
            safeRat(n * base - k, a), // multiplied the base by n instead of raising it to the power n
            n >= 0 && base !== 10 ? safeRat(ipow(n, base) - k, a) : null, // swapped base and exponent: nᵇ instead of bⁿ
            div(add(power, int(k)), int(a)), // sign slip moving k across the equals sign
            a !== 1 && whole !== null ? int(whole - k) : null, // forgot to divide by a
            power, // stopped at the argument's value (ax + k = bⁿ) without solving for x
          ],
          SOLVE_X,
        );
      },
      () => problem('\\log_{3}(x - 1) = 2', int(10), [int(7), int(9), int(8)], SOLVE_X),
    );
  },
};

// ───────────────────────────── g12.log-laws ─────────────────────────────

/** Non-power companions for base-10 product/quotient laws: log x + log 25 = 2 ⇒ x = 4. */
const BASE10_FACTORS = [2, 4, 5, 20, 25, 50] as const;

/**
 * log_b x + log_b m = n ⇒ x·m = bⁿ (and, above the gentle band, log_b x − log_b m = n ⇒ x = m·bⁿ).
 * m is a power of b, or a factor of a power of 10 for base-10 logs.
 */
const logLaws: ProblemTemplate = {
  id: 'g12.log-laws',
  grade: 12,
  title: 'Log laws: log_b x ± log_b m = n',
  parTimeMs: 20000,
  generate(rng, d) {
    const cap = scaled(d, 243, 1000);
    return retry(
      150,
      () => {
        const minus = d >= 0.35 && rng.chance(0.3 + 0.3 * d);
        const base = rng.pick(d < 0.35 ? [2, 3, 10] : [2, 3, 5, 10]);
        const oddFactor = base === 10 && d >= 0.5 && rng.chance(0.5);
        const mPow = rng.int(1, 2);
        const m = oddFactor ? rng.pick(BASE10_FACTORS) : ipow(base, mPow);
        const n = rng.int(1, minus ? 3 : 5);
        const power = ipow(base, n);
        const log = logOf(base);
        // The value of log_b x itself — what a learner who never converts back to x would give.
        const logX = oddFactor ? null : minus ? n + mPow : n - mPow;
        if (!minus) {
          if (power > cap || power % m !== 0) return null;
          const x = power / m;
          if (x < 2 || x === m) return null; // x = m would be guessable from the prompt
          const prompt = rng.chance(0.3) ? `${log} ${m} + ${log} x = ${n}` : `${log} x + ${log} ${m} = ${n}`;
          return problem(
            prompt,
            int(x),
            [
              int(power - m), // read log x + log m as log(x + m)
              power * m <= SHOWABLE ? int(power * m) : null, // multiplied by m instead of dividing
              logX !== null ? int(logX) : null, // stopped at log_b x = …, never converted back to x
            ],
            SOLVE_X,
          );
        }
        const x = m * power;
        if (x > cap) return null;
        return problem(
          `${log} x - ${log} ${m} = ${n}`,
          int(x),
          [
            int(power + m), // read log x − log m as log(x − m)
            safeRat(power, m), // divided by m instead of multiplying
            logX !== null ? int(logX) : null, // stopped at log_b x = …, never converted back to x
          ],
          SOLVE_X,
        );
      },
      () => problem('\\log_{2} x + \\log_{2} 4 = 5', int(8), [int(28), int(128), int(3)], SOLVE_X),
    );
  },
};

// ───────────────────────────── g12.geo-term ─────────────────────────────

/** Tₙ = a·rⁿ⁻¹ from a and r; negative ratios and r = ±1/2 (with a chosen so every term is whole) at the top. */
const geoTerm: ProblemTemplate = {
  id: 'g12.geo-term',
  grade: 12,
  title: 'Geometric sequence: find Tₙ from a and r',
  parTimeMs: 16000,
  generate(rng, d) {
    return retry(
      100,
      () => {
        const n = rng.int(d < 0.35 ? 4 : 5, scaled(d, 5, 8));
        let a: number;
        let r: Rational;
        if (d >= 0.6 && rng.chance(0.3)) {
          r = rat(rng.chance(0.3) ? -1 : 1, 2);
          a = rng.int(1, 3) * ipow(2, n - 1 + rng.int(0, 2));
        } else {
          r = int(rng.pick(d < 0.35 ? [2, 3] : d < 0.7 ? [2, 3, 4, -2] : [2, 3, 4, 5, -2, -3]));
          a = rng.int(1, scaled(d, 5, 8)) * (d >= 0.7 && rng.chance(0.2) ? -1 : 1);
        }
        if (Math.abs(a) > 1000) return null;
        const answer = mul(int(a), rpow(r, n - 1));
        if (Math.abs(answer.n) > 2000) return null;
        // (a·r)ⁿ⁻¹: multiplied a·r first, then raised to n − 1 (the classic slip with a whole ratio).
        const aThenPower = isInteger(r) ? cappedPow(mul(int(a), r), n - 1, SHOWABLE) : null;
        return problem(
          `a = ${a} \\sep r = ${ratMarkup(r)}`,
          answer,
          [
            mul(int(a), rpow(r, n)), // used n instead of n − 1 in Tₙ = a·rⁿ⁻¹
            mul(int(a), rpow(r, n - 2)), // one power short
            add(int(a), mul(int(n - 1), r)), // used the arithmetic formula a + (n − 1)d
            r.n < 0 ? neg(answer) : null, // lost the sign of a negative ratio
            aThenPower, // multiplied a·r first, then raised to n − 1
          ],
          { key: 'term', params: { n } },
        );
      },
      () => problem('a = 3 \\sep r = 2', int(96), [int(192), int(48), int(13)], { key: 'term', params: { n: 6 } }),
    );
  },
};

// ───────────────────────────── g12.arith-series ─────────────────────────────

/** Sₙ of an arithmetic series shown by its first three (positive) terms; negative d at the top. */
const arithSeries: ProblemTemplate = {
  id: 'g12.arith-series',
  grade: 12,
  title: 'Sum of an arithmetic series',
  parTimeMs: 22000,
  generate(rng, d) {
    const easy = d < 0.35;
    const mid = d < 0.7;
    return retry(
      100,
      () => {
        const a = rng.int(1, easy ? 6 : mid ? 15 : 30);
        const diff = easy ? rng.int(1, 4) : mid ? rng.int(2, 7) : rng.int(2, 9) * (rng.chance(0.4) ? -1 : 1);
        const n = easy ? rng.int(5, 10) : mid ? rng.int(8, 20) : rng.int(10, scaled(d, 20, 40));
        if (a + 2 * diff <= 0) return null; // the three terms shown stay positive
        const next = a + 3 * diff;
        const prompt = `${a}${seriesTerm(a + diff)}${seriesTerm(a + 2 * diff)}${next < 0 ? ' - ' : ' + '}\\dots`;
        if (!fitsPhone(prompt)) return null;
        const last = a + (n - 1) * diff;
        return problem(
          prompt,
          rat(n * (a + last), 2), // n(a + Tₙ) is always even: n even, or n odd ⇒ (n − 1)d even
          [
            safeRat(n * (2 * a + n * diff), 2), // used n instead of n − 1
            int(n * (a + last)), // forgot to halve
            int(last), // gave the last term Tₙ, not the sum
            safeRat(n * last, 2), // used a instead of 2a in n/2·(2a + (n − 1)d)
          ],
          { key: 'sum', params: { n } },
        );
      },
      () => problem('2 + 5 + 8 + \\dots', int(155), [int(170), int(310), int(29)], { key: 'sum', params: { n: 10 } }),
    );
  },
};

// ───────────────────────────── g12.geo-series ─────────────────────────────

/** Sₙ of a geometric series with a whole ratio (negative at the top), shown by its first three terms. */
const geoSeries: ProblemTemplate = {
  id: 'g12.geo-series',
  grade: 12,
  title: 'Sum of a geometric series',
  parTimeMs: 22000,
  generate(rng, d) {
    const easy = d < 0.35;
    const mid = d < 0.7;
    const sumMax = scaled(d, 500, 10000);
    return retry(
      100,
      () => {
        const a = rng.int(1, easy ? 5 : mid ? 5 : 6);
        const r = rng.pick(easy ? [2, 3] : mid ? [2, 3, 4] : [2, 3, 4, 5, -2, -3]);
        const n = easy ? rng.int(4, 5) : mid ? rng.int(5, 7) : rng.int(5, 8);
        /** a(rᵏ − 1)/(r − 1): exact, since r − 1 always divides rᵏ − 1. */
        const sumTo = (count: number) => (a * (ipow(r, count) - 1)) / (r - 1);
        const sum = sumTo(n);
        if (Math.abs(sum) > sumMax) return null;
        const prompt = `${a}${seriesTerm(a * r)}${seriesTerm(a * r * r)}${a * r * r * r < 0 ? ' - ' : ' + '}\\dots`;
        if (!fitsPhone(prompt)) return null;
        return problem(
          prompt,
          int(sum),
          [
            int(sumTo(n - 1)), // used rⁿ⁻¹ in the sum formula (one term short)
            int(sumTo(n + 1)), // one term too many
            int(a * ipow(r, n - 1)), // gave the nth term, not the sum
            int(a * (ipow(r, n) - 1)), // forgot to divide by r − 1
          ],
          { key: 'sum', params: { n } },
        );
      },
      () => problem('1 + 3 + 9 + \\dots', int(121), [int(40), int(364), int(81)], { key: 'sum', params: { n: 5 } }),
    );
  },
};

// ───────────────────────────── g12.function-eval ─────────────────────────────

/** A polynomial f with a non-zero constant: linear / ax² + c when gentle, full quadratics and cubics at the top. */
function functionTerms(rng: Rng, d: number): Term[] {
  const c = rng.intExcept(-9, 9, [0]);
  if (d < 0.35) return rng.chance(0.5) ? [[rng.int(2, 5), 1], [c, 0]] : [[rng.int(1, 3), 2], [c, 0]];
  if (d < 0.7) {
    const a = rng.intExcept(-3, 3, [0]);
    return rng.chance(0.5) ? [[a, 2], [c, 0]] : [[a, 2], [rng.intExcept(-6, 6, [0]), 1], [c, 0]];
  }
  switch (rng.int(0, 2)) {
    case 0:
      return [[rng.intExcept(-3, 3, [0]), 3], [c, 0]];
    case 1:
      return [[rng.pick([1, -1]), 3], [rng.intExcept(-6, 6, [0]), 1], [c, 0]];
    default:
      return [[rng.intExcept(-4, 4, [0]), 2], [rng.intExcept(-9, 9, [0]), 1], [c, 0]];
  }
}

/** Σ coef·power(p) over the terms — `power` lets a misconception swap in a wrong rule for xᵖ. */
function evalTerms(terms: readonly Term[], power: (p: number) => number): number {
  return terms.reduce((sum, [coef, p]) => sum + coef * power(p), 0);
}

/** f(x) given; evaluate f at a whole number (negative inputs from the middle band). */
const functionEval: ProblemTemplate = {
  id: 'g12.function-eval',
  grade: 12,
  title: 'Evaluate f(a)',
  parTimeMs: 14000,
  generate(rng, d) {
    return retry(
      100,
      () => {
        const terms = functionTerms(rng, d);
        const cubic = terms.some(([, p]) => p === 3);
        // At x = ±2, 2² = 2·2 hides the classic "x² read as 2x" slip, so squares start at 3.
        const minSize = terms.some(([, p]) => p === 2) ? 3 : 2;
        const size = rng.int(minSize, cubic ? 3 : d < 0.35 ? 4 : 5);
        const x = d < 0.35 || rng.chance(0.4) ? size : -size;
        const prompt = `f(x) = ${poly(terms)} \\sep f(${x}) = \\box`;
        if (!fitsPhone(prompt)) return null;
        const answer = evalTerms(terms, (p) => ipow(x, p));
        const constant = terms.find(([, p]) => p === 0)?.[0] ?? 0;
        // f(x) = mx + c: the linear shape has misconceptions of its own.
        const linear = terms.length === 2 && terms[0]![1] === 1 ? terms[0]![0] : null;
        return problem(prompt, int(answer), [
          x < 0 ? int(evalTerms(terms, (p) => (p > 0 && p % 2 === 0 ? -ipow(-x, p) : ipow(x, p)))) : null, // (−3)² taken as −9
          int(evalTerms(terms, (p) => (p === 0 ? 1 : x))), // ignored the powers: x² read as x
          int(evalTerms(terms, (p) => ipow(-x, p))), // substituted the opposite sign
          int(evalTerms(terms, (p) => (p >= 2 ? p * x : ipow(x, p)))), // x² read as 2x (power times base)
          int(terms.reduce((sum, [coef, p]) => sum + (p >= 2 ? ipow(coef * x, p) : coef * ipow(x, p)), 0)), // 2x² taken as (2x)²
          int(answer - 2 * constant), // sign slip on the constant term
          linear !== null ? int(linear + x + constant) : null, // read 4x as 4 + x
          linear !== null ? safeRat(x - constant, linear) : null, // solved f(x) = a instead of evaluating f(a)
        ]);
      },
      () => problem('f(x) = 2x^2 - 3 \\sep f(-3) = \\box', int(15), [int(-21), int(-9), int(33)]),
    );
  },
};

// ───────────────────────────── g12.inverse-eval ─────────────────────────────

/** f⁻¹(y) for linear f(x) = mx + c (or x/m + c near the top), built backwards from a whole-number answer. */
const inverseEval: ProblemTemplate = {
  id: 'g12.inverse-eval',
  grade: 12,
  title: 'Evaluate f⁻¹(y) for a linear f',
  parTimeMs: 18000,
  generate(rng, d) {
    const cMax = scaled(d, 8, 12);
    return retry(
      100,
      () => {
        const c = rng.intExcept(-cMax, cMax, [0]);
        if (d >= 0.6 && rng.chance(0.3)) {
          // f(x) = x/m + c, so f⁻¹(y) = m(y − c).
          const m = rng.int(2, 4) * (rng.chance(0.25) ? -1 : 1);
          const x = m * rng.intExcept(-4, 5, [0]);
          const y = x / m + c;
          const f = `${m < 0 ? '-' : ''}\\frac{x}{${Math.abs(m)}}${seriesTerm(c)}`;
          const prompt = `f(x) = ${f} \\sep f^{-1}(${y}) = \\box`;
          if (!fitsPhone(prompt)) return null;
          return problem(prompt, int(x), [
            safeRat(y + c * m, m), // evaluated f(y) instead of f⁻¹(y): y/m + c
            int(m * (y + c)), // sign slip undoing the constant
            int(m * y - c), // undid the steps in the wrong order: × m first, then − c
            safeRat(y - c, m), // divided by m instead of multiplying
            int(y - c), // undid the constant but forgot to multiply by m
          ]);
        }
        const m = d < 0.35 ? rng.int(2, 4) : rng.intExcept(-5, 5, [-1, 0, 1]);
        const x = d < 0.35 ? rng.int(1, 6) : rng.intExcept(-6, 9, [0]);
        const y = m * x + c;
        if (d < 0.35 && y <= 0) return null;
        const prompt = `f(x) = ${poly([[m, 1], [c, 0]])} \\sep f^{-1}(${y}) = \\box`;
        if (!fitsPhone(prompt)) return null;
        return problem(prompt, int(x), [
          int(m * y + c), // evaluated f(y) instead of f⁻¹(y)
          safeRat(y + c, m), // sign slip undoing the constant
          safeRat(y - c * m, m), // undid the steps in the wrong order: ÷ m first, then − c
          int(y - c), // undid the constant but forgot to divide by m
          m * y + c !== 0 ? safeRat(1, m * y + c) : null, // read f⁻¹ as 1/f
        ]);
      },
      () => problem('f(x) = 3x - 6 \\sep f^{-1}(9) = \\box', int(5), [int(21), int(1), int(9)]),
    );
  },
};

// ───────────────────────────── g12.sigma ─────────────────────────────

/** Σ_{k=lo}^{hi} (ak + b); lower limits above 1 and negative coefficients in the upper half. */
const sigma: ProblemTemplate = {
  id: 'g12.sigma',
  grade: 12,
  title: 'Sigma notation: sum of a linear expression',
  parTimeMs: 20000,
  generate(rng, d) {
    return retry(
      100,
      () => {
        const lo = d < 0.5 ? 1 : rng.pick([1, 1, 2, 3]);
        const count = d < 0.35 ? rng.int(3, 5) : rng.int(4, scaled(d, 8, 20));
        const hi = lo + count - 1;
        const aMax = scaled(d, 4, 6);
        const a = d < 0.35 ? rng.int(1, 3) : rng.intExcept(-aMax, aMax, [0]);
        const b = d < 0.35 ? rng.int(1, 5) : rng.intExcept(-9, 9, [0]);
        const term = (k: number) => a * k + b;
        let sum = 0;
        let kSum = 0;
        for (let k = lo; k <= hi; k++) {
          sum += term(k);
          kSum += k;
        }
        let fromOne = 0;
        for (let k = 1; k <= hi; k++) fromOne += term(k);
        const prompt = `\\Sigma_{k=${lo}}^{${hi}} (${poly([[a, 1], [b, 0]], 'k')}) = \\box`;
        if (!fitsPhone(prompt)) return null;
        return problem(prompt, int(sum), [
          int(sum - term(hi)), // counted hi − lo terms instead of hi − lo + 1
          int(sum + term(hi + 1)), // one term too many
          int(term(hi)), // evaluated only the last term
          int(a * kSum + b), // added the constant once instead of once per term
          lo > 1 ? int(fromOne) : null, // ignored the lower limit and started at k = 1
        ]);
      },
      () => problem('\\Sigma_{k=1}^{4} (2k + 1) = \\box', int(24), [int(15), int(35), int(9)]),
    );
  },
};

/** Grade 12 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE. */
export const GRADE_12: ProblemTemplate[] = [
  logEval,
  logEq,
  logLaws,
  geoTerm,
  arithSeries,
  geoSeries,
  functionEval,
  inverseEval,
  sigma,
];
